// src/components/ui/ContextMenu.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

/* ─── Icons ──────────────────────────────────────────────────────────────── */
const NewTabIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <path d="M6 2H2.5A1.5 1.5 0 0 0 1 3.5v10A1.5 1.5 0 0 0 2.5 15h10A1.5 1.5 0 0 0 14 13.5V10"/>
    <path d="M8 1h7v7M15 1 8 8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const SplitIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="1" y="2" width="14" height="12" rx="1.5"/>
    <line x1="8" y1="2" x2="8" y2="14" strokeLinecap="round"/>
  </svg>
);
const LeftPaneIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="1" y="2" width="14" height="12" rx="1.5"/>
    <line x1="8" y1="2" x2="8" y2="14" strokeLinecap="round"/>
    <rect x="1.5" y="2.5" width="5.5" height="11" rx="0.5" fill="currentColor" fillOpacity="0.35"/>
  </svg>
);
const RightPaneIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="1" y="2" width="14" height="12" rx="1.5"/>
    <line x1="8" y1="2" x2="8" y2="14" strokeLinecap="round"/>
    <rect x="8.5" y="2.5" width="6" height="11" rx="0.5" fill="currentColor" fillOpacity="0.35"/>
  </svg>
);
const SwapIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <path d="M1 5h10M8 2l3 3-3 3" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 11H5m3 3-3-3 3-3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const NewWindowIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="1" y="3" width="12" height="10" rx="1.5"/>
    <path d="M7 1h8v8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="m15 1-5 5" strokeLinecap="round"/>
  </svg>
);
const IncognitoIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <circle cx="8" cy="10" r="4"/>
    <path d="M5.5 7 8 4l2.5 3" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="6" cy="10" r="1" fill="currentColor" stroke="none"/>
    <circle cx="10" cy="10" r="1" fill="currentColor" stroke="none"/>
  </svg>
);
const CopyIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="5" y="5" width="9" height="9" rx="1.5"/>
    <path d="M2 11V2h9" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ExitSplitIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
    <rect x="1" y="2" width="14" height="12" rx="1.5"/>
    <path d="M8 6l-3 2 3 2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="2" x2="8" y2="14" strokeLinecap="round" strokeDasharray="2 2"/>
  </svg>
);

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function InlineToast({ msg }) {
  if (!msg) return null;
  return (
    <div className="mx-2 mb-1.5 px-3 py-2 bg-[#1a3a2a] border border-[#2d6a4a] rounded-md flex items-center gap-2 text-xs text-emerald-300 leading-tight">
      <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3 flex-shrink-0">
        <path d="M1.5 6l3 3 6-6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {msg}
    </div>
  );
}

function SectionLabel({ label }) {
  return (
    <div className="px-3.5 pt-2 pb-0.5 text-xs text-[#555] uppercase tracking-widest font-semibold">
      {label}
    </div>
  );
}

function copyText(text, showToast, msg) {
  const run = () => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.cssText = "position:fixed;opacity:0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      showToast(msg);
    } catch {
      showToast("Copy failed");
    }
  };
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => showToast(msg)).catch(run);
  } else {
    run();
  }
}

/* ─── Split-view state helpers ───────────────────────────────────────────────
   Pages may render inside an iframe (the split-view overlay).
   We always look at the TOP-level window for __splitView state, and dispatch
   events there too so the overlay picks them up.
────────────────────────────────────────────────────────────────────────────── */

/**
 * Safely climb to window.top (cross-origin iframes throw on access).
 * Returns the top window if same-origin, or null if cross-origin.
 */
function getTopWindow() {
  try {
    // If top is same origin we can access __splitView
    void window.top.location.href;
    return window.top;
  } catch {
    return null;
  }
}

/**
 * Return the __splitView object from whichever window owns it.
 * Prefers top-level window so iframe-hosted pages can read the state.
 */
function getSplitView() {
  const top = getTopWindow();
  if (top && top.__splitView) return top.__splitView;
  // Fallback: check parent (one level up)
  try {
    if (window.parent !== window && window.parent.__splitView) {
      return window.parent.__splitView;
    }
  } catch { /* cross-origin */ }
  // Fallback: same window (not inside an iframe)
  return window.__splitView ?? null;
}

/**
 * Dispatch an "open-split-view" event to the window that owns the overlay.
 */
function dispatchSplitEvent(detail) {
  const top = getTopWindow();
  const target = top ?? window.parent ?? window;
  try {
    target.dispatchEvent(new CustomEvent("open-split-view", { detail }));
  } catch {
    window.dispatchEvent(new CustomEvent("open-split-view", { detail }));
  }
}

/* ─── ContextMenu ────────────────────────────────────────────────────────── */
export function ContextMenu({ items, position, onClose }) {
  const menuRef  = useRef(null);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, delay = 1800) => {
    setToast(msg);
    setTimeout(() => { setToast(null); onClose(); }, delay);
  }, [onClose]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!position) return;
    const onDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    const t = setTimeout(() => {
      document.addEventListener("mousedown", onDown);
      document.addEventListener("keydown",   onKey);
    }, 10);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown",   onKey);
    };
  }, [position, onClose]);

  useEffect(() => { if (!position) setToast(null); }, [position]);

  const style = useCallback(() => {
    if (!position) return {};
    const W = 248;
    const H = items.reduce((acc, i) => acc + (i.section ? 26 : i.divider ? 9 : 38), 0)
            + (toast ? 52 : 0) + 12;
    return {
      position: "fixed",
      zIndex: 9999,
      top:  Math.max(4, position.y + H > window.innerHeight ? position.y - H : position.y),
      left: Math.max(4, position.x + W > window.innerWidth  ? position.x - W : position.x),
    };
  }, [position, items, toast]);

  if (!position) return null;

  return createPortal(
    <div
      ref={menuRef}
      style={style()}
      className="bg-[#242424] border border-[#363636] rounded-lg shadow-2xl py-1.5 min-w-[220px] overflow-hidden select-none"
      onContextMenu={(e) => e.preventDefault()}
    >
      <InlineToast msg={toast} />
      {items.map((item, i) => {
        if (item.section) return <SectionLabel key={i} label={item.section} />;
        if (item.divider) return <div key={i} className="border-t border-[#363636] my-1.5 mx-1" />;
        return (
          <button
            key={i}
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              item.action?.({ showToast });
              if (!item.toast) onClose();
            }}
            className={`w-full flex items-center gap-2.5 px-3.5 py-[7px] text-sm text-left transition-colors duration-75
              ${item.disabled
                ? "text-[#444] cursor-not-allowed"
                : item.active
                  ? "text-blue-300 bg-[#1a2f4a] hover:bg-[#1e3a5f] cursor-pointer"
                  : "text-[#d0d0d0] hover:bg-[#2e2e2e] hover:text-white cursor-pointer"
              }`}
          >
            {item.icon && (
              <span className={`flex-shrink-0 ${item.active ? "text-blue-400" : "text-[#777]"}`}>
                {item.icon}
              </span>
            )}
            <span className="flex-1 leading-none">{item.label}</span>
            {item.tag && (
              <span className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded font-medium ml-1">
                {item.tag}
              </span>
            )}
          </button>
        );
      })}
    </div>,
    document.body
  );
}

/* ─── useContextMenu hook ────────────────────────────────────────────────── */
export function useContextMenu() {
  const [menuPosition, setMenuPosition] = useState(null);
  const openMenu  = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPosition({ x: e.clientX, y: e.clientY });
  }, []);
  const closeMenu = useCallback(() => setMenuPosition(null), []);
  return { menuPosition, openMenu, closeMenu };
}

/* ─── buildNavContextItems ───────────────────────────────────────────────────
   Reads split-view state from the top-level window (works inside iframes too).
   
   Logic:
   • CASE A – Split view is active:
       We're either in the main frame or inside one of the two panes.
       → Show ONLY "Open in left pane" / "Open in right pane" + "Exit split view".
       → NEVER show "Open in split view" again (no duplicate split).
       → If the link IS the current pane's url, offer "Swap panes" instead.
   
   • CASE B – No split view:
       Standard browser-style menu with a single "Open in split view" entry.
────────────────────────────────────────────────────────────────────────────── */
export function buildNavContextItems(url) {
  const fullUrl = window.location.origin + url;

  // ── Read split-view state (works from inside iframes) ─────────────────────
  const sv = getSplitView();

  /* ══════════════════════════════════════════════════════════════════════════
     CASE A: Already in split view
  ══════════════════════════════════════════════════════════════════════════ */
  if (sv?.active) {
    const isCurrentLeft  = sv.leftUrl  === url || sv.leftUrl  === fullUrl;
    const isCurrentRight = sv.rightUrl === url || sv.rightUrl === fullUrl;
    const isEitherPane   = isCurrentLeft || isCurrentRight;

    return [
      /* ── Pane targets ──────────────────────────────────────────────────── */
      { section: "Open in split pane" },
      {
        label:    "Open in left pane",
        icon:     <LeftPaneIcon />,
        active:   isCurrentLeft,
        tag:      isCurrentLeft ? "current" : undefined,
        disabled: isCurrentLeft,
        action:   () => dispatchSplitEvent({ url, pane: "left" }),
      },
      {
        label:    "Open in right pane",
        icon:     <RightPaneIcon />,
        active:   isCurrentRight,
        tag:      isCurrentRight ? "current" : undefined,
        disabled: isCurrentRight,
        action:   () => dispatchSplitEvent({ url, pane: "right" }),
      },

      /* ── Swap — only useful when the link is already loaded in a pane ─── */
      ...(isEitherPane
        ? [{
            label:  "Swap panes",
            icon:   <SwapIcon />,
            action: () => {
              // Tell the overlay to swap left ↔ right
              const top = getTopWindow();
              const target = top ?? window.parent ?? window;
              try { target.__splitView?.swap?.(); } catch { /* noop */ }
              // Fallback: dispatch a swap event
              try {
                target.dispatchEvent(new CustomEvent("swap-split-panes"));
              } catch { /* noop */ }
            },
          }]
        : []),

      { divider: true },

      /* ── Exit ──────────────────────────────────────────────────────────── */
      {
        label:  "Exit split view",
        icon:   <ExitSplitIcon />,
        action: () => {
          try { getSplitView()?.close?.(); } catch { /* noop */ }
        },
      },

      { divider: true },

      /* ── Standard open options (no extra split-view item here) ─────────── */
      {
        label:  "Open in new tab",
        icon:   <NewTabIcon />,
        action: () => window.open(url, "_blank"),
      },
      {
        label:  "Open in new window",
        icon:   <NewWindowIcon />,
        action: () =>
          window.open(
            url,
            "_blank",
            "width=1280,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no",
          ),
      },

      { divider: true },

      {
        label:  "Copy link address",
        icon:   <CopyIcon />,
        toast:  true,
        action: ({ showToast }) => copyText(fullUrl, showToast, "Link copied!"),
      },
    ];
  }

  /* ══════════════════════════════════════════════════════════════════════════
     CASE B: Not in split view — standard browser-style menu
  ══════════════════════════════════════════════════════════════════════════ */
  return [
    {
      label:  "Open link in new tab",
      icon:   <NewTabIcon />,
      action: () => window.open(url, "_blank"),
    },
    {
      label:  "Open link in split view",
      icon:   <SplitIcon />,
      action: () => dispatchSplitEvent({ url, pane: "right" }),
    },
    {
      label:  "Open link in new window",
      icon:   <NewWindowIcon />,
      action: () =>
        window.open(
          url,
          "_blank",
          "width=1280,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no",
        ),
    },
    {
      label:  "Open link in incognito window",
      icon:   <IncognitoIcon />,
      toast:  true,
      action: ({ showToast }) =>
        copyText(fullUrl, showToast, "Link copied — paste it in an incognito window"),
    },
    { divider: true },
    {
      label:  "Copy link address",
      icon:   <CopyIcon />,
      toast:  true,
      action: ({ showToast }) => copyText(fullUrl, showToast, "Link copied!"),
    },
  ];
}